/*
 *
 * @Copyright 2023 UNLOCKIT DECENTRALIZATION, LDA
 * Development by VOID Software, SA
 *
 */

import Grid from '@mui/material/Grid';
import { FunctionComponent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as ListContractsSvg } from '../../assets/images/businesses.svg';
import { ReactComponent as CreateContractsSvg } from '../../assets/images/add-contract.svg';
import { ReactComponent as ListWorkflowsSvg } from '../../assets/images/workflow.svg';
import { ReactComponent as CreateWorkflowSvg } from '../../assets/images/add-workflow.svg';
import { AppRoute } from '../../constants/routes';
import { LinkActionItem } from '../../types/menu';
import { preparePageTitle } from '../../utils/route';
import { useAuthContext } from '../controllers/AuthenticationContext';
import { OrganizationsContext, withOrganizationsContext } from '../controllers/OrganizationsContext';
import { TranslationContext, withTranslationContext } from '../controllers/TranslationContext';
import { HomePendingInvites } from '../elements/HomePendingInvites';

type OwnProps = TranslationContext & OrganizationsContext;

const HomeScreenBase: FunctionComponent<OwnProps> = (props) => {
    const { organizationSelected, t } = props;
    const { user } = useAuthContext();

    useEffect(() => {
        document.title = preparePageTitle(t('menuBar.home'));
    }, []);

    const quickActionsMenu: LinkActionItem[] = organizationSelected?.organization
        ? [
            {
                title: 'createContract',
                icon: <CreateContractsSvg />,
                link: AppRoute.CreateContract,
            },
            {
                title: 'viewContracts',
                icon: <ListContractsSvg />,
                link: AppRoute.Contracts,
            },
            {
                title: 'createWorkflow',
                icon: <CreateWorkflowSvg />,
                link: AppRoute.CreateWorkflowSelectType,
            },
            {
                title: 'viewWorkflows',
                icon: <ListWorkflowsSvg />,
                link: AppRoute.OrganizationWorkflows,
            },
        ] : [
            {
                title: 'viewContracts',
                icon: <ListContractsSvg />,
                link: AppRoute.Contracts,
            },
            {
                title: 'viewWorkflows',
                icon: <ListWorkflowsSvg />,
                link: AppRoute.OrganizationWorkflows,
            }];
    const renderActionItem = (item: LinkActionItem) => {
        return (
            <div className="home-screen__quick-actions__card card">
                <div className="home-screen__quick-actions__card__icon-wrap">
                    {item.icon}
                </div>
                <span className="home-screen__quick-actions__card__title">
                    {t(`homeScreen.${item.title}`)}
                </span>
            </div>
        );
    };
    return (
        <div className="home-screen">
            <h1>{`${t('homeScreen.welcome')}, ${user?.fullName}`}</h1>
            <section className="home-screen__pending-invites-wrapper">
                <HomePendingInvites />
            </section>
            <section className="home-screen__quick-actions">
                <h3>{t('homeScreen.quickActionsTitle')}</h3>
                <Grid container spacing={2} data-testid="quick-actions-list">
                    {quickActionsMenu.map((item) => (
                        <Grid item xs={6} md={4} lg={3} key={item.title}>
                            <Link
                                to={item.link}
                                target={item.newTab ? '_blank' : '_self'}
                                data-testid={`link-${item.title}`}
                            >
                                {renderActionItem(item)}
                            </Link>
                        </Grid>
                    ))}
                </Grid>
            </section>
        </div>
    );
};

export const HomeScreen = withOrganizationsContext(withTranslationContext(HomeScreenBase));
